/**
 * A deploy removes the previous hashed route chunks, so a tab left open across
 * it requests files that no longer exist; the rejected import would unmount the
 * app into the root error boundary.
 *
 * Recovery: retry once (network blip), then reload to fetch the current
 * index.html. The reload is stamped in sessionStorage; a second failure inside
 * the cooldown means the origin is unreachable, so rethrow instead of looping.
 */

import { lazy, type ComponentType } from "react";
import * as Sentry from "@sentry/react";

const RELOAD_STAMP = "chunk_reload_at";
const RELOAD_COOLDOWN_MS = 10000;
const RETRY_DELAY_MS = 300;

// Every engine words this differently: Chrome/Edge, Firefox, Safari, and Vite's
// own CSS preloader in that order.
const CHUNK_ERROR =
  /failed to fetch dynamically imported module|error loading dynamically imported module|importing a module script failed|unable to preload css/i;

function isChunkError(err: unknown): boolean {
  const message = err instanceof Error ? err.message : String(err ?? "");
  return CHUNK_ERROR.test(message);
}

/** True when reloading is unsafe: we already did it, or we cannot tell. */
function reloadBlocked(): boolean {
  try {
    const at = Number(sessionStorage.getItem(RELOAD_STAMP) ?? 0);
    return at > 0 && Date.now() - at < RELOAD_COOLDOWN_MS;
  } catch {
    return true; // no storage means no loop guard, so do not reload at all
  }
}

function stampReload(): void {
  try {
    sessionStorage.setItem(RELOAD_STAMP, String(Date.now()));
  } catch {
    // Unreachable: reloadBlocked() already refused when storage throws.
  }
}

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Drop-in replacement for React.lazy() for route-level chunks.
 * Same signature, same return value — only the failure path differs.
 */
export function lazyRoute<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
) {
  return lazy(async () => {
    try {
      return await factory();
    } catch (err) {
      if (!isChunkError(err)) throw err;

      await wait(RETRY_DELAY_MS);
      try {
        return await factory();
      } catch (retryErr) {
        if (!isChunkError(retryErr) || reloadBlocked()) throw retryErr;

        stampReload();
        // A warning, not an error: the user is about to get the page; a spike
        // means deploys are stranding sessions.
        Sentry.captureException(retryErr, {
          level: "warning",
          tags: { chunk_reload: "true" },
        });
        await Sentry.flush(1500);
        window.location.reload();

        // reload() does not stop execution; a never-settling promise keeps
        // Suspense on its fallback instead of flashing the error boundary.
        return await new Promise<never>(() => {});
      }
    }
  });
}
